import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getCompanyLimitedStats } from '../services/api/CasesApi';
import { useAuth } from './AuthContext';

const CaseLimitContext = createContext();

export const CaseLimitProvider = ({ children }) => {
  const { company, accessToken } = useAuth();
  const [caseLimitData, setCaseLimitData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Fetch case limit data
  const fetchCaseLimitData = useCallback(async (forceRefresh = false) => {
    // Don't fetch if not authenticated
    if (!company || !accessToken) {
      console.log('CaseLimitContext: Skipping fetch - not authenticated');
      return null;
    }

    // Prevent duplicate calls within 1 second unless forced
    const now = Date.now();
    if (!forceRefresh && lastFetchTime && now - lastFetchTime < 1000) {
      return caseLimitData;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getCompanyLimitedStats();
      const data = response?.data;
      
      setCaseLimitData(data);
      setLastFetchTime(Date.now());
      
      return data;
    } catch (err) {
      console.error('CaseLimitContext: Error fetching case limit data:', err);
      setError(err.message || 'Failed to fetch case limit data');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [lastFetchTime, caseLimitData, company, accessToken]);

  // Refresh function - forces a new fetch
  const refreshCaseLimitData = useCallback(async () => {
    return await fetchCaseLimitData(true);
  }, [fetchCaseLimitData]);

  // Check if adding new cases would exceed limit
  const checkCaseLimit = useCallback((casesToAdd = 1) => {
    if (!caseLimitData) {
      return {
        canAdd: false,
        reason: 'Case limit data not loaded',
        currentTotal: 0,
        limit: 0,
        available: 0,
      };
    }

    const currentTotal = Number(caseLimitData?.cases?.totalCases || 0);
    const limit = Number(caseLimitData?.limit?.caseLimit || 0);
    const available = limit - currentTotal;
    const canAdd = currentTotal + casesToAdd <= limit;

    return {
      canAdd,
      reason: canAdd ? 'Within limit' : 'Would exceed case limit',
      currentTotal,
      limit,
      available,
      wouldExceedBy: canAdd ? 0 : (currentTotal + casesToAdd - limit),
    };
  }, [caseLimitData]);

  // Get formatted stats for display
  const getFormattedStats = useCallback(() => {
    if (!caseLimitData) return null;

    return {
      // Users
      users: {
        total: caseLimitData.users?.total || 0,
        limit: caseLimitData.limit?.usersLimit || 0,
        percentage: caseLimitData.users?.total && caseLimitData.limit?.usersLimit
          ? ((caseLimitData.users.total / caseLimitData.limit.usersLimit) * 100).toFixed(1)
          : 0,
      },
      // Total Cases
      cases: {
        total: caseLimitData.cases?.totalCases || 0,
        limit: caseLimitData.limit?.caseLimit || 0,
        available: (caseLimitData.limit?.caseLimit || 0) - (caseLimitData.cases?.totalCases || 0),
        percentage: caseLimitData.cases?.totalCases && caseLimitData.limit?.caseLimit
          ? ((caseLimitData.cases.totalCases / caseLimitData.limit.caseLimit) * 100).toFixed(1)
          : 0,
      },
      // High Court
      highCourt: {
        total: caseLimitData.cases?.totalHighCourtCases || 0,
        percentage: caseLimitData.cases?.totalCases > 0
          ? ((caseLimitData.cases.totalHighCourtCases / caseLimitData.cases.totalCases) * 100).toFixed(1)
          : 0,
      },
      // District Court
      districtCourt: {
        total: caseLimitData.cases?.totalDistrictCourtCases || 0,
        percentage: caseLimitData.cases?.totalCases > 0
          ? ((caseLimitData.cases.totalDistrictCourtCases / caseLimitData.cases.totalCases) * 100).toFixed(1)
          : 0,
      },
      // Other Courts
      otherCourt: {
        total: caseLimitData.cases?.totalOtherCourtCases || 0,
        percentage: caseLimitData.cases?.totalCases > 0
          ? ((caseLimitData.cases.totalOtherCourtCases / caseLimitData.cases.totalCases) * 100).toFixed(1)
          : 0,
      },
      // Supreme Court
      supremeCourt: {
        total: caseLimitData.cases?.totalSupremeCourtCases || 0,
        percentage: caseLimitData.cases?.totalCases > 0
          ? ((caseLimitData.cases.totalSupremeCourtCases / caseLimitData.cases.totalCases) * 100).toFixed(1)
          : 0,
      },
    };
  }, [caseLimitData]);

  // Fetch on mount and when authentication changes
  useEffect(() => {
    // Only fetch if user is authenticated
    if (company && accessToken) {
      fetchCaseLimitData();
    } else {
      // Clear data if not authenticated
      setCaseLimitData(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, accessToken]);

  const value = {
    // State
    caseLimitData,
    isLoading,
    error,
    lastFetchTime,
    
    // Methods
    fetchCaseLimitData,
    refreshCaseLimitData,
    checkCaseLimit,
    getFormattedStats,
  };

  return (
    <CaseLimitContext.Provider value={value}
    >
      {children}
    </CaseLimitContext.Provider>
  );
};

export const useCaseLimit = () => {
  const context = useContext(CaseLimitContext);
  if (!context) {
    throw new Error('useCaseLimit must be used within a CaseLimitProvider');
  }
  return context;
};
